/*
 * Copyright (c) 2020 Staffino, s.r.o. - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Created by Martin Komara, September 2020
 */

import React, { useRef, useCallback, useState, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Choice, Question } from '@staffino/model';
import { Box, convertCssUnit } from '@staffino/primitive-controls';
import { ScrollAnchor, useScrollEffect } from '../scroll';
import Header from '../header';
import { MultiChoiceControl } from '../controls';
import ChoiceTextInputControl from '../controls/choice-text-input-control';
import isEnterPressed from '../input-submit';
import asFix from '../components/as-fix.js';

type Props = {
  blockId: string;
  question: Question;
  choices: Choice[];
  otherChoice?: Choice;
  titleSize: Question['extraParams']['titleSize'];
  limit?: number;
  minChoiceLimit?: number;
  maxChoiceLimit?: number;
  required?: boolean;
  colorStyle?: string;
} & React.ComponentProps<typeof Box>;

const ChoiceTextInputControlFix = asFix(ChoiceTextInputControl);

const ChoiceQuestionMultiSelectFragment = ({
  blockId,
  question,
  choices = [],
  otherChoice,
  titleSize,
  limit,
  minChoiceLimit,
  maxChoiceLimit,
  required,
  colorStyle,
}: Props) => {
  const { t } = useTranslation();
  const context = useFormContext();

  const MultiChoiceControlComponent = context ? Controller : MultiChoiceControl;
  const ChoiceTextInputControlComponent = context
    ? Controller
    : ChoiceTextInputControl;

  const selectedChoiceIds = context?.watch(`${blockId}.choiceIds`);
  const checkedCount = (selectedChoiceIds || [])?.filter(Boolean).length;

  const totalOptionLength = choices.length + (otherChoice ? 1 : 0);

  const choiceError =
    context?.errors[blockId]?.choiceIds || // for more options set
    context?.errors[blockId]?.choiceIds?.[0]; // for one option set

  const isMinLimitValid = !!minChoiceLimit && minChoiceLimit > 0;
  const isMaxLimitValid = !!maxChoiceLimit && maxChoiceLimit > 0;
  const isMinRuleValid =
    isMinLimitValid &&
    (!isMaxLimitValid || minChoiceLimit <= maxChoiceLimit) &&
    minChoiceLimit <= totalOptionLength;
  const isMaxRuleValid =
    isMaxLimitValid &&
    (!isMinLimitValid || maxChoiceLimit >= minChoiceLimit) &&
    maxChoiceLimit <= totalOptionLength;

  const maxChoicesExceeded =
    isMaxRuleValid && // evaluate this only if limit is valid
    [...choices, otherChoice]?.filter(Boolean)?.filter((ch) => !!ch.title)
      ?.length > maxChoiceLimit &&
    selectedChoiceIds?.length >= maxChoiceLimit;

  const cannotSelectMore =
    isMaxRuleValid && // evaluate this only if limit is valid
    maxChoiceLimit !== undefined &&
    checkedCount >= maxChoiceLimit;

  const choiceValidation = {
    validate: {
      minChoiceLimitRule: (values) =>
        !isMinRuleValid || // don't apply rule if limit is invalid
        !minChoiceLimit ||
        (!required && values?.length === 0) || // not required; more choices
        (!required && typeof values === 'boolean') || // not required; 1 choice
        (typeof values === 'boolean' && values) || // 1 choice; and it's selected
        values?.length >= minChoiceLimit,
      maxChoiceLimitRule: (values) =>
        !isMaxRuleValid || // don't apply rule if limit is invalid
        !maxChoiceLimit ||
        typeof values === 'boolean' || // 1 choice
        values?.length <= maxChoiceLimit,
      required: (values) =>
        !required ||
        (typeof values === 'boolean' && values) || // 1 choice; and it's selected
        values?.length > 0,
    },
  };

  const answerErrorMessage = useMemo(() => {
    if (choiceError) {
      if (
        choiceError.type === 'required' || // for more options set
        choiceError[0]?.type === 'required' // for one option set
      ) {
        return t('Please answer this question.');
      }
      if (
        choiceError.type === 'minChoiceLimitRule' ||
        choiceError[0]?.type === 'minChoiceLimitRule'
      ) {
        return t('Please choose at least {{minChoiceLimit}} options.', {
          minChoiceLimit: minChoiceLimit,
        });
      }
      if (
        choiceError.type === 'maxChoiceLimitRule' ||
        choiceError[0]?.type === 'maxChoiceLimitRule'
      ) {
        return t('The maximum number of options is selected.');
      }
    } else if (maxChoicesExceeded) {
      return t('The maximum number of options is selected.');
    }
    return null;
  }, [minChoiceLimit, choiceError, maxChoicesExceeded, t]);

  const commentRef = useRef(null);
  const commentValue = context?.watch(`${blockId}.comment`);
  const [commentHighlighted, setCommentHighlighted] = useState(false);

  const isChoiceDisabled = useCallback(
    (choiceId: string) =>
      cannotSelectMore && !(selectedChoiceIds || [])?.includes(choiceId),
    [cannotSelectMore, selectedChoiceIds],
  );

  const forceOptionArrayAppendix = totalOptionLength === 1 ? '[0]' : '';

  const [enterTriggered, setEnterTriggered] = useState(false);
  const { scrollToError } = useScrollEffect(
    blockId,
    enterTriggered ? 'validValue' : null,
  );

  return (
    <>
      <ScrollAnchor id={blockId} />
      <Header
        question={question}
        size={titleSize}
        required={required}
        answerErrorMessage={answerErrorMessage}
        answerErrorMessageType={
          !choiceError && maxChoicesExceeded ? 'info' : 'error'
        }
      />
      <MultiChoiceControlComponent
        as={
          // there is an issue with passing onFocus directly in Controller in react-hook-form v5
          <MultiChoiceControl
            ref={context?.register}
            onFocus={() => scrollToError(blockId)}
          />
        }
        key={`${blockId}${forceOptionArrayAppendix}`}
        name={`${blockId}.choiceIds${forceOptionArrayAppendix}`}
        control={context?.control}
        rules={choiceValidation}
        choices={choices}
        otherChoice={otherChoice}
        colorStyle={colorStyle}
        isDisabled={isChoiceDisabled}
        commentHighlighted={commentHighlighted}
        onFocusOtherChoice={() => {
          if (!commentValue) {
            // even if it is not required, we need to encourage customer to fill it
            setCommentHighlighted(true);
            setTimeout(() => commentRef?.current?.focus());
          } else {
            scrollToError(blockId);
          }
        }}
        onBlurOtherChoice={() => {
          setCommentHighlighted(false);
          setTimeout(() => commentRef?.current?.blur());
        }}
        mt={titleSize === 'small' ? 0 : convertCssUnit('1.25rem')}
        otherChoiceInput={
          otherChoice && (
            <ChoiceTextInputControlComponent
              as={ChoiceTextInputControlFix}
              refFix={(element: any) => {
                context?.register?.(element);
                commentRef.current = element;
              }}
              onFocusFix={() => setCommentHighlighted(true)}
              name={`${blockId}.comment`}
              control={context?.control}
              placeholder={otherChoice.title || t('Other')}
              onKeyDown={(event) => {
                if (!selectedChoiceIds?.includes(otherChoice.id)) {
                  context?.setValue(`${blockId}.choiceIds`, [
                    otherChoice.id,
                    ...(selectedChoiceIds ?? []),
                  ]);
                }
                setEnterTriggered(isEnterPressed(event));
                // if (isEnterPressed(event)) {
                //   setTimeout(() => commentRef?.current?.blur());
                // }
              }}
              onBlur={() => setCommentHighlighted(false)}
              maxLength={limit}
              disabled={isChoiceDisabled(otherChoice.id)}
            />
          )
        }
      />
    </>
  );
};

export default ChoiceQuestionMultiSelectFragment;
